import type { CreateFormData } from "@/components/create/types";
import type { AgentWidget, PresetOption } from "@/types/createAgent";
import {
  getCategoryGridActionId,
  getChoicePresets,
  getPresetPresentationType,
  isVibeBundlePreset,
} from "@/lib/agent/presetPresentation";
import { getWidgetPresets } from "@/lib/agent/widgetPresets";
import { normalizeWidgetType } from "@/lib/agent/widgetPlan";
import { GENERATE_BACK_LABEL, GENERATE_FRONT_LABEL } from "@/lib/agent/labels";
import {
  getActiveWidgetSurfacePresets,
  isBackDesignChoice,
  isFrontReviewDecisionPresetSurface,
} from "@/lib/agent/visiblePresetCapabilities";

/**
 * Back cards in a select-then-commit surface are only a local preference.
 * Some legacy authored choices already carry workflow flags; explicitly clear
 * them so highlighting a card cannot approve the front or request a render.
 */
export function buildLocalBackChoiceSelectionPreset(
  preset: PresetOption
): PresetOption {
  if (!isBackDesignChoice(preset)) return preset;

  const {
    frontApproved: _ignoredFrontApproval,
    generateBack: _ignoredDataGenerateBack,
    ...data
  } = (preset.data ?? {}) as Partial<CreateFormData>;
  const { generateBack: _ignoredActionGenerateBack, ...actionData } =
    preset.actionData ?? {};
  return {
    ...preset,
    data: { ...data, frontApproved: false, generateBack: false },
    actionData,
  };
}

export function withFrontApprovalForBackChoice(
  preset: PresetOption,
  context: { currentPhase: string; hasFrontImage: boolean }
): PresetOption {
  if (
    !isBackDesignChoice(preset) ||
    !context.hasFrontImage ||
    context.currentPhase !== "front_review" ||
    preset.actionData?.exploreMore === true
  ) {
    return preset;
  }

  return {
    ...preset,
    data: {
      ...(preset.data || {}),
      frontApproved: true,
      generateBack: true,
    },
    actionData: {
      ...(preset.actionData || {}),
      generateBack: true,
    },
  };
}

/** Exact secondary footer action persisted in the server-owned surface. */
export function buildExplorePreset(preset: PresetOption): PresetOption {
  const {
    frontApproved: _ignoredFrontApproval,
    generateBack: _ignoredDataGenerateBack,
    ...data
  } = (preset.data ?? {}) as Partial<CreateFormData>;
  const { generateBack: _ignoredGenerateBack, ...actionData } =
    preset.actionData ?? {};
  const safeData = isBackDesignChoice(preset)
    ? { ...data, frontApproved: false, generateBack: false }
    : data;
  return {
    ...preset,
    data: safeData,
    actionData: { ...actionData, exploreMore: true },
  };
}

/** Exact no-selection footer action rendered below a selectable catalog. */
export function buildNoneSelectedFooterPreset(): PresetOption {
  return {
    label: "None of these fit",
    action: "suggest",
    actionId: "custom",
    data: {},
    actionData: { exploreMore: true, noneSelected: true },
  };
}

/** Exact credit-bearing footer action persisted in the server-owned surface. */
export function buildGenerateFooterPreset(
  preset: PresetOption,
  context: { currentPhase: string; hasFrontImage: boolean }
): PresetOption {
  const effectivePreset = withFrontApprovalForBackChoice(preset, context);
  const shouldGenerateBack =
    context.hasFrontImage &&
    (context.currentPhase === "back_design" ||
      context.currentPhase === "back_review" ||
      isBackDesignChoice(effectivePreset));

  return {
    label: shouldGenerateBack ? GENERATE_BACK_LABEL : GENERATE_FRONT_LABEL,
    action: "action",
    actionId: shouldGenerateBack
      ? "generate_dress_back"
      : "generate_dress_front",
    presetType: "commit_action",
    data: effectivePreset.data ?? {},
    actionData: {
      ...(effectivePreset.actionData || {}),
      generateType: shouldGenerateBack ? "back" : "front",
      ...(shouldGenerateBack ? { generateBack: true } : {}),
    },
  };
}

/**
 * Builds every exact footer payload the current response can render. These
 * are stored only in the service-owned action surface, not returned as extra
 * buttons, so the UI and verifier share one canonical payload contract.
 */
export function buildPersistedFooterActions(params: {
  presets?: PresetOption[];
  widgets?: AgentWidget[];
  currentPhase: string;
  hasFrontImage: boolean;
}): PresetOption[] {
  const actions: PresetOption[] = [];
  const addExploreActions = (choices: PresetOption[]) => {
    actions.push(...choices.map(buildExplorePreset));
  };
  const addSelectionFooterActions = (
    choices: PresetOption[],
    options: { includeNoneSelected?: boolean } = {}
  ) => {
    actions.push(
      ...choices.flatMap(choice => [
        buildGenerateFooterPreset(choice, params),
        buildExplorePreset(choice),
      ])
    );
    if (options.includeNoneSelected !== false) {
      actions.push(buildNoneSelectedFooterPreset());
    }
  };

  // Widgets are renderer-authoritative. When they exist, message-level
  // presets are compatibility data and must not grant hidden capabilities.
  if ((params.widgets ?? []).length > 0) {
    const widgets = params.widgets ?? [];
    const surfacePresets = getActiveWidgetSurfacePresets(widgets);
    if (isFrontReviewDecisionPresetSurface(surfacePresets)) {
      // The composite renderer always opens back choices as a local selector,
      // even when the authored field picker contains only 2–3 choices. Persist
      // the exact derived footer payloads that can follow that selection.
      addSelectionFooterActions(getChoicePresets(surfacePresets), {
        includeNoneSelected: false,
      });
    } else {
      for (const widget of widgets) {
        if (!widget.active) continue;
        const widgetType = normalizeWidgetType(widget.type);
        if (
          ![
            "concept_picker",
            "field_picker",
            "catalog_picker",
            "upload_request",
          ].includes(widgetType)
        ) {
          continue;
        }
        const choices = getChoicePresets(getWidgetPresets(widget));
        if (choices.length === 0) continue;
        if (choices.every(isVibeBundlePreset)) {
          // Vibe cards themselves invoke the explore action; they have no
          // Generate/None footer.
          addExploreActions(choices.slice(0, 3));
          continue;
        }
        const isUpload = choices.every(
          choice => getPresetPresentationType(choice) === "upload_choice"
        );
        if (isUpload) continue;
        const sharedGridAction = getCategoryGridActionId(choices);
        const rendersCatalogFooter =
          widgetType === "catalog_picker" ||
          Boolean(sharedGridAction && choices.length > 5);
        const isCuratedOneTap =
          widgetType === "field_picker" && !rendersCatalogFooter;
        if (!isCuratedOneTap) addSelectionFooterActions(choices);
      }
    }
  } else {
    const presets = params.presets ?? [];
    const choices = getChoicePresets(presets);
    if (choices.every(isVibeBundlePreset) && choices.length > 0) {
      addExploreActions(choices.slice(0, 3));
    } else {
      const sharedGridAction = getCategoryGridActionId(choices);
      const rendersCatalogFooter = Boolean(
        sharedGridAction && choices.length > 5
      );
      const rendersFrontReviewBackFooter =
        isFrontReviewDecisionPresetSurface(presets);
      if (rendersCatalogFooter || rendersFrontReviewBackFooter) {
        addSelectionFooterActions(choices, {
          includeNoneSelected: !rendersFrontReviewBackFooter,
        });
      }
    }
  }

  const seen = new Set<string>();
  return actions.filter(action => {
    const key = JSON.stringify(action);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}
